import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BodyScrollEndEvent, GridApi, GridOptions } from 'ag-grid-community';
import {
  AbsoluteScrollPosition,
  Row,
  ScrollPosition,
  ScrollToRowScrollPosition,
  ScrollableProps,
  UseControlledAGGridProps,
} from '../types';

/**
 * Type guard to check if scrollable props include onSync callback.
 * If true, it's a SyncedScrollProps with full scroll position control.
 * If false, it's a ScrollToRowProps for simple scroll-to-row functionality.
 */
const hasSyncCallback = <RowId extends string = string>(
  scrollable: ScrollableProps<RowId>
): scrollable is ScrollableProps<RowId> & {
  onSync: (scrollPosition: AbsoluteScrollPosition) => void;
} => {
  return 'onSync' in scrollable;
};

/**
 * Type guard to check if scroll position uses row-based scrolling.
 * Returns true if scrollPosition has a rowId property with a string value.
 */
const isRowIdScrollPosition = <RowId extends string = string>(
  scrollPosition: ScrollPosition<RowId> | ScrollToRowScrollPosition<RowId>
): scrollPosition is { rowId: RowId; left?: number } => {
  return 'rowId' in scrollPosition && typeof scrollPosition.rowId === 'string';
};

/**
 * Type guard to check if scroll position uses absolute pixel-based scrolling.
 * Returns true if scrollPosition has both top and left properties.
 */
const isAbsoluteScrollPosition = <RowId extends string = string>(
  scrollPosition: ScrollPosition<RowId> | ScrollToRowScrollPosition<RowId>
): scrollPosition is { top: number; left: number } => {
  return 'top' in scrollPosition && 'left' in scrollPosition;
};

/**
 * When retrieving scroll state via `api.getState().scroll`, AG Grid returns
 * `undefined` instead of `{ top: 0, left: 0 }` when the grid is scrolled to
 * the top-left origin position.
 *
 * This helper normalizes AG Grid's scroll state by treating `undefined` as `0`
 * for both top and left values.
 */
const getScrollPosition = (api: GridApi): AbsoluteScrollPosition => {
  const scroll = api.getState().scroll;

  return {
    top: scroll?.top ?? 0,
    left: scroll?.left ?? 0,
  };
};

export const useScrollable = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>,
  api: GridApi | null
) => {
  const scrollable = props.scrollable;
  const scrollableEnabled = scrollable !== undefined;
  const scrollableRef = useRef(scrollable);
  useEffect(() => {
    scrollableRef.current = scrollable;
  }, [scrollable]);
  const isUpdatingFromProps = useRef(false);

  const scrollPosition = useMemo(
    () => (scrollable ? scrollable.scrollPosition : null),
    [scrollable]
  );

  // Sync external scroll position → AG Grid
  useEffect(() => {
    if (!api || !scrollableEnabled || !scrollPosition) {
      return;
    }

    if (isRowIdScrollPosition(scrollPosition)) {
      // Scroll a specific row into view
      const rowNode = api.getRowNode(scrollPosition.rowId);
      if (rowNode) {
        api.ensureNodeVisible(rowNode);
      }

      // If we have onSync, sync the absolute position back after the scroll settles
      // This converts { rowId, left } → { top, left } in the parent state
      const currentScrollable = scrollableRef.current;
      if (currentScrollable && hasSyncCallback(currentScrollable)) {
        isUpdatingFromProps.current = true;
        requestAnimationFrame(() => {
          const scrollFromState = getScrollPosition(api);
          currentScrollable.onSync({
            top: scrollFromState.top,
            left: scrollFromState.left,
          });
          isUpdatingFromProps.current = false;
        });
      }
    } else if (isAbsoluteScrollPosition(scrollPosition)) {
      // Scroll to absolute position using Grid State API
      const currentScroll = getScrollPosition(api);

      const verticalChanged = currentScroll.top !== scrollPosition.top;
      const horizontalChanged = currentScroll.left !== scrollPosition.left;

      if (verticalChanged || horizontalChanged) {
        // Block sync to avoid feedback loop when setting absolute position
        isUpdatingFromProps.current = true;

        api.setState({
          scroll: {
            top: scrollPosition.top,
            left: scrollPosition.left,
          },
        });

        // Reset the flag after a short delay to allow the scroll event to fire
        requestAnimationFrame(() => {
          isUpdatingFromProps.current = false;
        });
      }
    }
  }, [api, scrollableEnabled, scrollPosition]);

  const scrollableGridProps: GridOptions<Row<RowData, RowId>> | undefined =
    scrollable
      ? {
          onBodyScrollEnd: (event: BodyScrollEndEvent<Row<RowData, RowId>>) => {
            // Don't sync back if we're updating from props
            if (isUpdatingFromProps.current) {
              return;
            }

            const currentScrollable = scrollableRef.current;
            if (!currentScrollable || !hasSyncCallback(currentScrollable)) {
              return;
            }

            currentScrollable.onSync(getScrollPosition(event.api));
          },
        }
      : undefined;

  const handleInitialScroll = useCallback(
    (api: GridApi) => {
      if (!scrollableEnabled || !scrollPosition) {
        return;
      }

      if (isRowIdScrollPosition(scrollPosition)) {
        const rowNode = api.getRowNode(scrollPosition.rowId);
        if (rowNode) {
          api.ensureNodeVisible(rowNode);
        }
      } else if (isAbsoluteScrollPosition(scrollPosition)) {
        api.setState({
          scroll: {
            top: scrollPosition.top,
            left: scrollPosition.left,
          },
        });
      }
    },
    [scrollableEnabled, scrollPosition]
  );

  return { scrollableGridProps, handleInitialScroll };
};
